import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useT } from '@/store/i18n';

interface Props {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  width?: number; // px
}

export function Drawer({ open, onClose, title, children, width = 400 }: Props) {
  const t = useT();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9985] flex justify-end animate-fade-in"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" />
      <div
        role="dialog"
        aria-modal="true"
        className="relative h-full bg-white shadow-2xl border-l border-slate-200 flex flex-col animate-[slide-down_0.18s_ease-out]"
        style={{ width: `min(${width}px, 100vw)` }}
      >
        {(title || true) && (
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-slate-900 truncate">
              {title}
            </div>
            <button
              type="button"
              aria-label={t('aria.close')}
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-1.5 rounded-md flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
}
